import React from "react";
import { Link, useLocation } from "react-router-dom";
import { COLORS } from "@/lib/colors";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import logo from "@/assets/logo_dpu.png";

/**
 * Simple layout providing a top or side nav.
 * For minimal changes, just a horizontal nav with links.
 *
 * Children is the page content displayed below the nav.
 */

const NAV_ITEMS = [
  { path: "/", label: "Home" },
  { path: "/dashboard", label: "Dashboard" },
  { path: "/metrics", label: "Metrics & Factors" },
  { path: "/rankings", label: "Rankings" },
  { path: "/scenario", label: "Scenario Builder" },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  return (
    <div className="min-h-screen h-screen flex flex-col bg-gray-50">
      {/* Modern Header / Navbar */}
      <header className="bg-[#172744] shadow-lg flex-none">
        <div className="mx-auto max-w-7xl px-4 py-3">
          <div className="flex justify-between items-center">
            {/* Logo with subtle hover effect */}
            <Link 
              to="/" 
              className="relative group flex items-center gap-3"
            >
              <img 
                src={logo} 
                alt="DePaul University Logo" 
                className="h-8 w-auto"
              />
              <span className="text-xl font-bold text-white group-hover:opacity-90 transition-opacity">
                DePaul Rankings Simulator
              </span>
              <motion.div
                className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#FCE58E] group-hover:w-full transition-all duration-300"
                whileHover={{ width: "100%" }}
              />
            </Link>

            {/* Enhanced Modern Navigation */}
            <nav className="flex items-center gap-3">
              {NAV_ITEMS.map((item) => {
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      "relative px-4 py-2 text-sm font-medium transition-all duration-200",
                      "hover:text-white group",
                      isActive
                        ? "text-white"
                        : "text-blue-100"
                    )}
                  >
                    <span className="relative">
                      {item.label}
                      
                      {/* Hover underline effect */}
                      <span className={cn(
                        "absolute inset-x-0 -bottom-1 h-0.5 bg-[#FCE58E] transform origin-left transition-transform duration-200",
                        "group-hover:scale-x-100",
                        isActive ? "scale-x-100" : "scale-x-0"
                      )} />
                    </span>
                    
                    {/* Active state background */}
                    {isActive && (
                      <motion.div
                        layoutId="navIndicator"
                        className="absolute inset-0 bg-white/10 rounded-lg -z-10"
                        initial={false}
                        transition={{
                          type: "spring",
                          stiffness: 380,
                          damping: 30
                        }}
                      />
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
}